import React, { useEffect, useContext } from 'react';
import { format, isAfter, addMonths } from 'date-fns';
import { DateInput } from './DateInput';
import styles from './DateRangeInput.module.scss';
import { useTranslation } from 'react-i18next';
import { DatepickerContext } from './context/context';
import { ReactComponent as InfoIcon } from '../../assets/info.svg';

interface DateRangeInputProps {
  onStartDateChange?: (date: string | null) => void;
  onEndDateChange?: (date: string | null) => void;
  className?: string;
}

export const DateRangeInput: React.FC<DateRangeInputProps> = ({
  onStartDateChange,
  onEndDateChange,
  className = '',
}) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(DatepickerContext);

  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.length !== 10) return null;

    const [day, month, year] = dateStr.split('/').map(Number);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }
    return null;
  };

  const handleStartDateChange = (date: string | null) => {
    onStartDateChange?.(date);
  };

  const handleEndDateChange = (date: string | null) => {
    onEndDateChange?.(date);
  };

  const dispatchRangeError = (error: string) => {
    dispatch({ type: 'SET_RANGE_ERROR', payload: error });
  };

  useEffect(() => {
    let error = '';

    if (state.startDate && isAfter(new Date(2020, 0, 1), state.startDate)) {
      error = t('Start date cannot be earlier than 01/01/2020');
    } else if (state.endDate && isAfter(state.endDate, addMonths(new Date(), 1))) {
      error = t('End date cannot be later than next month');
    } else if (state.isRange && state.startDate && state.endDate) {
      const start = parseDate(format(state.startDate, 'dd/MM/yyyy'));
      const end = parseDate(format(state.endDate, 'dd/MM/yyyy'));
      if (start && end && isAfter(start, end)) {
        error = t('Start date cannot be later than end date');
      }
    }

    dispatchRangeError(error);
  }, [state.startDate, state.endDate]);

  return (
    <div className={`${className}`}>
      <div className={styles.dateRangeTitle}>
        {state.isRange ? t('Date Range') : t('Start date')}
      </div>
      <div className={styles.dateRangeContainer}>
        <DateInput
          value={state.startDate ? format(state.startDate, 'dd/MM/yyyy') : ''}
          onChange={handleStartDateChange}
          placeholder="DD/MM/YYYY"
          className="flex-1"
          validationError={state.startDateError}
          id="start-date"
          clearError={() => {
            dispatch({ type: 'SET_START_DATE_ERROR', payload: null });
          }}
        />

        {state.isRange && (
          <>
            <span className={styles.hyphen}>-</span>
            <DateInput
              value={state.endDate ? format(state.endDate, 'dd/MM/yyyy') : ''}
              onChange={handleEndDateChange}
              placeholder="DD/MM/YYYY"
              className="flex-1"
              validationError={state.endDateError}
              id="end-date"
              clearError={() => {
                dispatch({ type: 'SET_END_DATE_ERROR', payload: null });
              }}
              setFocus={
                state.isRange && state.startDate !== null && !state.endDate
                  ? true
                  : false
              }
            />
          </>
        )}
      </div>

      {state.rangeError && (
        <div className={styles.rangeError}>
          <InfoIcon className={styles.infoIcon} />
          {state.rangeError}
        </div>
      )}
    </div>
  );
};
